/**
 * 通用格式化工具
 *
 * 大数字格式化 + 动态字号缩小
 *
 * 用法：
 *   import { numFmt, createSizeFn, numSize, largeNumSize, numFmtSize } from '@/utils/fmt'
 *
 *   numFmt(1234567)         → '123.5万'
 *   numSize('123.5万')      → '12px'
 *   largeNumSize('123.5万') → '22px'
 *   numFmtSize(1234567)     → { text: '123.5万', fontSize: '12px' }
 *
 * 自定义字号阶梯：
 *   const mySize = createSizeFn([[6,'16px'],[8,'14px'],[10,'12px']], '10px')
 *   mySize('123.5万') → '14px'
 */

// ── 大数字格式化 ──────────────────────────────────────────

/** 数值≥1e8 转亿, ≥1e4 转万; 均保留一位小数; 小数值保留整数 */
export function numFmt(v) {
  const n = Number(v) || 0
  if (n >= 1e8) return (n / 1e8).toFixed(1) + '亿'
  if (n >= 1e4) return (n / 1e4).toFixed(1) + '万'
  // ponytail: 始终返回字符串，避免模板中 undefined/数字 导致渲染异常
  return String(Math.round(n))
}

// ── 动态字号缩小 ──────────────────────────────────────────

/**
 * 根据字符串长度阶梯缩小字号的工厂函数
 * @param {Array<[number, string]>} steps - [[maxLen, fontSize], ...]，按长度升序
 * @param {string} fallback - 超过最后一步时的兜底字号
 * @returns {(str: string) => string}
 *
 * ponytail: 阈值阶梯是贪心匹配——第一个满足 len <= maxLen 的步生效
 */
export function createSizeFn(steps, fallback = '10px') {
  return str => {
    const len = String(str).length
    for (const [maxLen, fontSize] of steps) {
      if (len <= maxLen) return fontSize
    }
    return fallback
  }
}

/** 小字号阶梯（列表内紧凑场景）：12px → 11px → 10px */
export const numSize = createSizeFn([[6, '12px'], [8, '11px']], '10px')

/** 大字号阶梯（卡片/饼图大字场景）：22px → 18px → 15px → 12px */
export const largeNumSize = createSizeFn([[6, '22px'], [8, '18px'], [10, '15px']], '12px')

/**
 * 无极字号：根据字符串长度在 [minPx, maxPx] 之间线性插值
 * @param {string} str - 要测量长度的文本
 * @param {number} [minPx=12] - 最短文本对应的字号
 * @param {number} [maxPx=22] - 最短文本对应的字号
 * @param {number} [maxLen=10] - 超过此长度后锁定为 minPx
 * @returns {string} 字号值，如 "18.5px"
 *
 * ponytail: 假设文本宽度 ≈ 长度 × 字宽，所以按长度线性缩放。
 * maxLen 对应格式化后最长可能文本（如 "1234.5万" 共 8 字符，"12.3亿" 共 6 字符）。
 */
export function fluidSize(str, minPx = 12, maxPx = 22, maxLen = 10) {
  const len = Math.min(String(str).length, maxLen)
  // ponytail: 最短文本（如 "0"）用 maxPx，每多一个字减一点
  const ratio = len <= 1 ? 1 : (maxLen - len) / (maxLen - 1)
  const px = maxPx - (maxPx - minPx) * (1 - ratio)
  return px.toFixed(1) + 'px'
}

// ── 合调用 ──────────────────────────────────────────

/** 格式化 + 小字号 */
export function numFmtSize(v) {
  const text = numFmt(v)
  return { text, fontSize: numSize(text) }
}

/** 格式化 + 大字号 */
export function numFmtLargeSize(v) {
  const text = numFmt(v)
  return { text, fontSize: largeNumSize(text) }
}
